<template>
  <div>
    <b-modal
      :id="modalId"
      hide-header
      hide-footer
      centered
      size="lg"
      @show="open"
      @hidden="resetForm"
    >
    <h6 class="title">{{ editingId ? "Editar" : "Cadastrar" }} {{ title }}</h6>
    <div class="input-group-check">
      <label>Nome <span>*</span></label>
      <b-input
        ref="nameInput"
        v-model.trim="name"
        maxlength="150"
        :disabled="loading"
        @keyup.enter="save"
      />
    </div>
    <div class="actions">
      <button class="secondary" :disabled="loading" @click="cancel">
        {{ editingId ? "Cancelar edição" : "Fechar" }}
      </button>
      <button class="primary" :disabled="loading" @click="save">
        <b-spinner v-if="loading" small />
        <span v-else>{{ editingId ? "Atualizar" : "Salvar" }}</span>
      </button>
    </div>

    <div class="catalog-list">
      <div class="list-heading">
        <div>
          <h6>{{ listTitle }}</h6>
          <small>Edite ou exclua os registros cadastrados</small>
        </div>
        <span v-if="!loadingList" class="total">{{ items.length }}</span>
      </div>

      <div v-if="loadingList" class="list-state">
        <b-spinner small />
        <span>Carregando registros...</span>
      </div>

      <div v-else-if="!items.length" class="list-state empty">
        Nenhum registro cadastrado.
      </div>

      <div v-else class="table-wrapper">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th class="actions-column">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.nome }}</td>
              <td>
                <div class="row-actions">
                  <button
                    type="button"
                    title="Editar"
                    :disabled="loading || Boolean(deletingId)"
                    @click="edit(item)"
                  >
                    <b-icon-pencil-square />
                  </button>
                  <button
                    type="button"
                    class="delete"
                    title="Excluir"
                    :disabled="loading || Boolean(deletingId)"
                    @click="openDeleteConfirmation(item)"
                  >
                    <b-spinner v-if="deletingId === item.id" small />
                    <b-icon-trash v-else />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </b-modal>
    <ModalConfirm
      :modalName="deleteModalId"
      title="Deseja realmente excluir ?"
      :subtitle="deleteSubtitle"
      leftButtonText="Não excluir"
      rightButtonText="Sim"
      headerIcon="b-icon-exclamation-triangle-fill"
      :rightButtonClick="remove"
      @modalClosed="clearPendingDelete"
    />
  </div>
</template>

<script>
import ServiceCheckBank from "../../services/serviceCheckBank";
import ServiceCheckCostCenter from "../../services/serviceCheckCostCenter";
import ModalConfirm from "../Modals/ModalConfirm.vue";

export default {
  components: { ModalConfirm },
  props: {
    modalId: { type: String, required: true },
    catalogType: { type: String, required: true },
    title: { type: String, required: true },
  },
  data() {
    return {
      name: "",
      items: [],
      editingId: "",
      deletingId: "",
      pendingDelete: null,
      loading: false,
      loadingList: false,
    };
  },
  computed: {
    service() {
      return this.catalogType === "banks"
        ? ServiceCheckBank
        : ServiceCheckCostCenter;
    },
    listTitle() {
      return this.catalogType === "banks"
        ? "Bancos cadastrados"
        : "Centros de Custos cadastrados";
    },
    deleteModalId() {
      return `${this.modalId}DeleteConfirm`;
    },
    deleteSubtitle() {
      const name = this.pendingDelete?.nome;
      return name
        ? `Atenção! Esta ação não poderá ser desfeita. Ao confirmar, “${name}” será excluído permanentemente.`
        : "Atenção! Esta ação não poderá ser desfeita.";
    },
  },
  methods: {
    open() {
      this.resetForm();
      this.loadItems();
    },
    close() {
      this.$bvModal.hide(this.modalId);
    },
    cancel() {
      if (this.editingId) this.resetForm();
      else this.close();
    },
    resetForm() {
      this.name = "";
      this.editingId = "";
    },
    sortItems() {
      this.items.sort((first, second) =>
        first.nome.localeCompare(second.nome, "pt-BR", { sensitivity: "base" })
      );
    },
    async loadItems() {
      this.loadingList = true;
      try {
        this.items = (await this.service.findAll()) || [];
        this.sortItems();
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            `Não foi possível carregar ${this.listTitle.toLowerCase()}`,
          type: "info",
        });
      } finally {
        this.loadingList = false;
      }
    },
    edit(item) {
      this.editingId = item.id;
      this.name = item.nome;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
    async save() {
      if (!this.name) {
        return this.$toast.open({ message: "Informe o nome", type: "info" });
      }
      this.loading = true;
      try {
        const isEditing = Boolean(this.editingId);
        if (isEditing) {
          const item = { id: this.editingId, nome: this.name };
          await this.service.update(item);
          const index = this.items.findIndex(({ id }) => id === item.id);
          if (index >= 0) this.$set(this.items, index, item);
          this.$emit("updated", item);
        } else {
          const item = await this.service.save({ nome: this.name });
          this.items.push(item);
          this.$emit("created", item);
        }
        this.sortItems();
        this.$toast.open({
          message: `${this.title} ${isEditing ? "atualizado" : "cadastrado"} com sucesso!`,
          type: "success",
        });
        this.resetForm();
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            `Não foi possível salvar ${this.title.toLowerCase()}`,
          type: "info",
        });
      } finally {
        this.loading = false;
      }
    },
    openDeleteConfirmation(item) {
      if (!item?.id || this.loading || this.deletingId) return;
      this.pendingDelete = item;
      this.$bvModal.show(this.deleteModalId);
    },
    clearPendingDelete() {
      if (!this.deletingId) this.pendingDelete = null;
    },
    async remove() {
      const item = this.pendingDelete;
      if (!item?.id || this.loading || this.deletingId) return;
      this.deletingId = item.id;
      try {
        await this.service.delete(item.id);
        this.items = this.items.filter(({ id }) => id !== item.id);
        if (this.editingId === item.id) this.resetForm();
        this.$emit("deleted", item.id);
        this.$toast.open({
          message: `${this.title} excluído com sucesso!`,
          type: "success",
        });
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            `Não foi possível excluir ${this.title.toLowerCase()}`,
          type: "info",
        });
      } finally {
        this.deletingId = "";
        this.pendingDelete = null;
      }
    },
  },
};
</script>

<style scoped>
.title { color: #3a4750; font-weight: 600; margin-bottom: 20px; }
.input-group-check label { color: #666360; font-size: 12px; font-weight: 600; }
.input-group-check label span { color: #c41515; }
.actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
.actions button { border: 0; border-radius: 4px; min-width: 90px; padding: 7px 14px; }
.actions button:disabled, .row-actions button:disabled { cursor: not-allowed; opacity: 0.55; }
.secondary { background: #eceaea; color: #3a4750; }
.primary { background: #006256; color: white; }
.catalog-list { border-top: 1px solid #eceaea; margin-top: 24px; padding-top: 20px; }
.list-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.list-heading h6 { color: #3a4750; font-weight: 600; margin: 0 0 2px; }
.list-heading small { color: #888; }
.total { display: flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 8px; border-radius: 14px; background: #e4f0ee; color: #006256; font-weight: 600; }
.table-wrapper { max-height: 300px; overflow: auto; border: 1px solid #eceaea; border-radius: 4px; }
.catalog-table { width: 100%; }
.catalog-table th { position: sticky; top: 0; padding: 10px 12px; background: #f1eeee; color: #666360; font-size: 12px; }
.catalog-table td { padding: 10px 12px; border-top: 1px solid #eceaea; color: #5c5855; }
.actions-column { width: 110px; text-align: center; }
.row-actions { display: flex; justify-content: center; gap: 14px; }
.row-actions button { border: 0; background: transparent; color: #3a4750; }
.row-actions .delete { color: #c41515; }
.list-state { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 90px; color: #777; border: 1px solid #eceaea; border-radius: 4px; }
.list-state.empty { background: #fafafa; }
@media (max-width: 576px) {
  .actions { align-items: stretch; flex-direction: column-reverse; }
  .actions button { width: 100%; }
  .catalog-table th, .catalog-table td { padding: 9px 8px; }
  .actions-column { width: 82px; }
}
</style>
